import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import authService from '../services/auth.service';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';

import LogoImage from '../assets/1.png';

type Step = 'email' | 'code' | 'password';

export default function ForgotPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [passwords, setPasswords] = useState({ password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(t.auth.errors.invalidEmail);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword({ email });
      setStep('code');
      setCountdown(60);
      setSuccess(t.auth.forgotPassword?.codeSent || 'If this email is registered, a reset code will be sent.');
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError(t.auth.verification?.tooManyRequests || 'Please wait before requesting a new code');
      } else {
        // Always show success to prevent email enumeration
        setStep('code');
        setCountdown(60);
        setSuccess(t.auth.forgotPassword?.codeSent || 'If this email is registered, a reset code will be sent.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError(t.auth.verification?.invalidCode || 'Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await authService.verifyResetCode({ email, code });
      setStep('password');
    } catch (error: any) {
      setError(error.response?.data?.detail || t.auth.verification?.invalidCode || 'Invalid or expired reset code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwords.password) {
      setError(t.auth.errors.requiredField);
      return;
    }
    
    if (passwords.password.length < 8) {
      setError(t.auth.errors.weakPassword);
      return;
    }
    
    if (passwords.password !== passwords.confirmPassword) {
      setError(t.auth.errors.passwordMismatch);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.resetPassword({
        email,
        code: code,
        new_password: passwords.password,
      });
      
      setSuccess(t.auth.forgotPassword?.resetSuccess || 'Password reset successfully!');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      if (error.response?.status === 400) {
        setError(t.auth.verification?.invalidCode || 'Invalid or expired reset code');
        setStep('code');
        setCode('');
      } else {
        setError(t.auth.errors.networkError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword({ email });
      setSuccess(t.auth.forgotPassword?.codeSent || 'A new reset code has been sent.');
      setCountdown(60);
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError(t.auth.verification?.tooManyRequests || 'Please wait before requesting a new code');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F4EEFA] via-white to-[#E8F8F5] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)',
          }}
        />
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#B18AE0] to-[#5B3FD0] rounded-full opacity-20"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-[#9FFFCB] opacity-20 rounded-xl rotate-45"></div>
        <div className="absolute bottom-32 left-20 w-14 h-14 bg-[#5B3FD0] rounded-full opacity-15"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-3 group mb-8">
            <img src={LogoImage} alt="EduNasser Logo" className="w-32 h-32 object-contain" />
          </Link>

          {/* Icon */}
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#5B3FD0] to-[#B18AE0] rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-[#2C2C2C]">
            {step === 'email' && (t.auth.forgotPassword?.title || 'Forgot Password')}
            {step === 'code' && (t.auth.forgotPassword?.enterCode || 'Enter Reset Code')}
            {step === 'password' && (t.auth.forgotPassword?.newPassword || 'Set New Password')}
          </h2>
          <p className="mt-2 text-gray-600">
            {step === 'email' && (t.auth.forgotPassword?.subtitle || 'Enter your email to receive a reset code')}
            {step === 'code' && (t.auth.forgotPassword?.codeSubtitle || 'Enter the 6-digit code sent to your email')}
            {step === 'password' && (t.auth.forgotPassword?.passwordSubtitle || 'Create a new secure password')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 space-y-6">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#B18AE0] via-[#5B3FD0] to-[#9FFFCB] rounded-t-3xl"></div>

          {error && (
            <div className="bg-red-50/80 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50/80 border border-green-400 text-green-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </div>
            </div>
          )}

          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <Input
                label={t.auth.login.email}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={loading}
              />

              <Button
                type="submit"
                disabled={loading}
                className="w-full shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>{t.common.loading}</span>
                  </div>
                ) : (
                  <>
                    {t.auth.forgotPassword?.sendCode || 'Send Reset Code'}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Code */}
          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className="space-y-6">
              <p className="text-center text-sm text-gray-600">
                {t.auth.forgotPassword?.sentTo || 'Code sent to'}{' '}
                <span className="font-semibold text-[#5B3FD0]">{email}</span>
              </p>

              <div className="space-y-4">
                <Input
                  label={t.auth.forgotPassword?.enterCode || 'Verification Code'}
                  type="text"
                  name="code"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                  }}
                  placeholder="123456"
                  required
                  disabled={loading}
                  className="text-center text-2xl tracking-[0.5em] font-bold"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>{t.common.loading}</span>
                  </div>
                ) : (
                  t.auth.forgotPassword?.verifyCode || 'Verify Code'
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {t.auth.verification?.noCode || "Didn't receive the code?"}{' '}
                  {countdown > 0 ? (
                    <span className="text-gray-400">
                      {t.auth.verification?.resendIn || 'Resend in'} {countdown}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading}
                      className="text-[#5B3FD0] hover:text-[#B18AE0] font-bold disabled:opacity-50"
                    >
                      {t.auth.verification?.resendButton || 'Resend Code'}
                    </button>
                  )}
                </p>
              </div>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <Input
                label={t.auth.register.password}
                type="password"
                name="password"
                value={passwords.password}
                onChange={(e) => setPasswords(prev => ({ ...prev, password: e.target.value }))}
                required
                autoComplete="new-password"
                disabled={loading}
              />

              <Input
                label={t.auth.register.confirmPassword}
                type="password"
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                autoComplete="new-password"
                disabled={loading}
              />

              <Button
                type="submit"
                disabled={loading}
                className="w-full shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>{t.common.loading}</span>
                  </div>
                ) : (
                  <>
                    {t.auth.forgotPassword?.resetButton || 'Reset Password'}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#707070] hover:text-[#5B3FD0] font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t.auth.verification?.backToLogin || 'Back to Login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
